import React, { useState, useRef } from 'react';

import PropTypes from 'prop-types';

import {
  VideoWrapper,
  Video,
  VideoControls,
  PlayButton,
  ProgressNumbers,
  ProgressBar,
  YoutubeIframe,
} from './styles';
import { defaultTheme } from './theme';

const padTo2Digits = (num) => num.toString().padStart(2, '0');

const SourceVideoPlayer = ({ src, autoPlay, muted, loop, controls, theme }) => {
  const [progress, setProgress] = useState({
    percent: 0,
    milliseconds: 0,
  });
  const [isPlaying, setPlaying] = useState(true);
  const videoRef = useRef();

  const handleVideo = () => {
    setPlaying(!isPlaying);
    isPlaying ? videoRef.current.pause() : videoRef.current.play();
  };

  const handleVideoProgress = (event) => {
    const manualChange = Number(event.target.value);
    videoRef.current.currentTime =
      (videoRef.current.duration / 100) * manualChange;
    setPlaying(manualChange);
  };

  const handleOnTimeUpdate = () => {
    const percent =
      (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress({
      percent: percent,
      milliseconds: videoRef.current.currentTime * 1000,
    });
  };

  const time = {
    seconds: padTo2Digits(Math.floor(progress.milliseconds / 1000)),
    minutes: padTo2Digits(Math.floor(progress.milliseconds / 1000 / 60)),
  };

  return (
    <VideoWrapper theme={theme}>
      <Video
        ref={videoRef}
        playsInline
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        onTimeUpdate={handleOnTimeUpdate}
      >
        <source src={src} type="video/mp4" />
      </Video>
      {controls && (
        <VideoControls>
          <PlayButton onClick={handleVideo}>
            <svg height="12" width="12" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5.1339745962156 1.3708348754011a1 1 0 0 1 1.7320508075689 0l4.7679491924311 8.2583302491977a1 1 0 0 1 -0.86602540378444 1.5l-9.5358983848622 0a1 1 0 0 1 -0.86602540378444 -1.5"
                stroke="none"
              ></path>
            </svg>
          </PlayButton>
          <ProgressNumbers>
            {time.minutes}:{time.seconds}
          </ProgressNumbers>
          <ProgressBar
            max="100"
            min="0"
            type="range"
            value={progress.percent}
            onChange={(e) => handleVideoProgress(e)}
          />
        </VideoControls>
      )}
    </VideoWrapper>
  );
};

const VideoPlayer = ({
  src,
  youtubeEmbedId,
  autoPlay,
  muted,
  loop,
  controls,
  theme: _theme,
}) => {
  const theme = { ...defaultTheme, ..._theme };
  if (youtubeEmbedId) {
    return (
      <YoutubeIframe
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        frameBorder="0"
        src={`https://www.youtube.com/embed/${youtubeEmbedId}`}
        title="Embedded youtube"
      />
    );
  }

  return (
    <SourceVideoPlayer
      playsInline
      autoPlay={autoPlay}
      controls={controls}
      loop={loop}
      muted={muted}
      src={src}
      theme={theme}
    />
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string,
  youtubeEmbedId: PropTypes.string,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  controls: PropTypes.bool,
};

export default VideoPlayer;
